import { Icon, Select, SegmentedButton, Typography, Button } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AppLoader, Pagination, UploadImageButton } from '@hero/atoms';
import { UploadImageModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import { DIGITAL_WRAPPER, WRAPPERS_TAB } from '@hero/constants';
import { ImagePreview } from '@hero/molecules';
import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';
import { useGetBranding, useUpdateBranding } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { deleteAllGiftWrapperSchema } from '@hero/validations';

import { BrandingLoader } from '../BrandingLoader';

import PreviewSection from './PreviewSection';
import * as S from './style';

const MAX_GIFT_WRAPPER = 5;

function DigitalWrapper({ branding, onUpdateData, isUpdating, defaultOccasion, isPage = true }) {
  const LIMIT = 14;

  const { t } = useTranslation();
  const { showNotification, showSuccess } = useNotification();

  //? Featured Wrapper - Start
  const [featuredPageIndex, setFeaturedPageIndex] = useState(1);
  const [featuredOccasion, setFeaturedOccasion] = useState();

  const { data: brandingData, isFetching: isFetchingBranding } = useGetBranding();
  const { mutateAsync: updateBranding } = useUpdateBranding();

  const { data: dataOccasionsCategories } = useGetCSEntries({
    entryCode: 'sgb_admin_occasion_category',
    params: {
      asc: 'order',
      query: { visibility: true }
    }
  });

  const allOccasionCategoryIds = dataOccasionsCategories?.entries?.map(oc => oc.uid);
  const { data: dataDigitalWrappers, isFetching: isFetchingDigitalWrappers } = useGetCSEntries({
    entryCode: 'sgb_admin_occasion',
    params: {
      pageIndex: featuredPageIndex,
      occasion: featuredOccasion?.value,
      limit: LIMIT,
      // TODO: Add sorting functionality for digital wrappers by category
      //asc: 'occasion_category.order',
      query: {
        'occasion_category.uid': {
          $in: featuredOccasion?.value
            ? allOccasionCategoryIds?.filter(ocId => ocId === featuredOccasion?.value)
            : allOccasionCategoryIds
        }
      }
    }
  });
  const occasionOptions = dataOccasionsCategories?.entries?.map(oc => ({
    label: oc.label,
    value: oc.uid
  }));

  const giftWrapperUrl = branding?.giftWrapperUrl || brandingData?.branding?.giftWrappers?.default;

  const [activeTab, setActiveTab] = useState(WRAPPERS_TAB.featured);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const hasOccasionOptions =
    occasionOptions?.some(option => !!option.label && !!option.value) &&
    activeTab === WRAPPERS_TAB.featured;

  //? Library Wrapper - Start
  const [libraryPageIndex, setLibraryPageIndex] = useState(1);
  const [libraryWrappers, setLibraryWrappers] = useState([]);

  const library = useMemo(() => {
    const brandingLibrary = brandingData?.branding?.giftWrappers?.library || [];
    return [...new Set(brandingLibrary)];
  }, [brandingData]);

  const uploadAreaDisabled = library?.length === MAX_GIFT_WRAPPER;

  useEffect(() => {
    if (library?.length === 0) return setLibraryWrappers([]);

    let currentDigitalWrappers = library?.slice(
      (libraryPageIndex - 1) * LIMIT,
      libraryPageIndex * LIMIT
    );

    setLibraryWrappers(currentDigitalWrappers);
  }, [library, libraryPageIndex]);
  //? Library Wrapper - End

  const onSelect = async url => {
    if (onUpdateData) await onUpdateData({ giftWrapperUrl: url });
  };

  const onDelete = url => {
    showNotification({
      title: t('areYouSureYouWantDelete', {
        type: t('giftWrapper').toLocaleLowerCase()
      }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      onSubmit: async () => {
        await updateBranding({
          branding: { giftWrappers: { library: library.filter(item => item !== url) } }
        });

        showSuccess({
          message: (
            <Trans
              i18nKey="giftWrapperRemovedSuccessfully"
              values={{ length: 1 }}
              components={{ b: <b /> }}
            />
          ),
          type: 'toast'
        });
      },
      primaryButtonProps: { color: 'error', children: t('removeItem') },
      secondaryButtonProps: { children: t('keepItem') }
    });
  };

  const handleDeleteAll = () => {
    showNotification({
      title: t('deleteAllGiftWrapper'),
      message: t('deleteAllGiftWrapperDesc'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('deleteAllGiftWrapperConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: deleteAllGiftWrapperSchema,
      onSubmit: async () => {
        await updateBranding({
          branding: { giftWrappers: { library: [] } }
        });

        showSuccess({
          message: (
            <Trans
              i18nKey="giftWrapperRemovedSuccessfully"
              values={{ length: libraryWrappers?.length }}
              components={{ b: <b /> }}
            />
          ),
          type: 'toast'
        });
      },
      primaryButtonProps: { color: 'error', children: t('yesDelete') },
      secondaryButtonProps: { children: t('cancel') }
    });
  };

  useEffect(() => {
    if (!!libraryWrappers?.length && !giftWrapperUrl) {
      onSelect(libraryWrappers[0]);
    }
  }, [giftWrapperUrl, libraryWrappers]);

  useEffect(() => {
    setFeaturedOccasion(occasionOptions?.find(occasion => occasion.value === defaultOccasion));
  }, [defaultOccasion, hasOccasionOptions]);

  const wrapperOptions = [
    {
      value: WRAPPERS_TAB.featured,
      label: t('catalogWrappers'),
      children: (
        <>
          <S.ImagePreviewWrapper hasWraps={!!dataDigitalWrappers?.entries?.length > 0}>
            {isFetchingDigitalWrappers ? (
              <BrandingLoader count={LIMIT} />
            ) : (
              dataDigitalWrappers?.entries?.map(wrapper => (
                <ImagePreview
                  hasPreview
                  src={wrapper.image.url}
                  active={giftWrapperUrl === wrapper.image.url}
                  key={wrapper.image.url}
                  isWrapper={true}
                  onClick={() => {
                    onSelect(wrapper.image.url);
                  }}
                />
              ))
            )}
            {isUpdating && <AppLoader />}
          </S.ImagePreviewWrapper>
          {dataDigitalWrappers?.pagination?.hasPagination && (
            <Pagination
              pageIndex={featuredPageIndex}
              setPageIndex={setFeaturedPageIndex}
              limit={LIMIT}
              totalDocs={dataDigitalWrappers.pagination?.total}
            />
          )}
        </>
      )
    },
    {
      value: WRAPPERS_TAB.saved,
      label: t('myWrappers'),
      children: (
        <>
          <S.ImagePreviewWrapper hasWraps={libraryWrappers?.length > 0}>
            <UploadImageButton onUpload={() => setIsUploadModalOpen(true)} />
            {isFetchingBranding ? (
              <BrandingLoader count={1} />
            ) : (
              libraryWrappers.map(item => (
                <ImagePreview
                  hasPreview
                  key={item}
                  src={item}
                  active={giftWrapperUrl === item}
                  onDelete={() => onDelete(item)}
                  isWrapper={true}
                  showActions
                  onClick={() => {
                    onSelect(item);
                  }}
                />
              ))
            )}
          </S.ImagePreviewWrapper>
          {libraryWrappers?.length > 0 && (
            <Pagination
              pageIndex={libraryPageIndex}
              setPageIndex={setLibraryPageIndex}
              limit={LIMIT}
              totalDocs={libraryWrappers?.length}
            />
          )}

          <UploadImageModal
            isOpen={isUploadModalOpen}
            setIsOpen={setIsUploadModalOpen}
            title={t('uploadYourDigitalWrappers')}
            uploadAction={async ({ cdnUrl } = {}) => {
              await updateBranding({
                branding: {
                  giftWrappers: {
                    default: giftWrapperUrl,
                    library: [...new Set([cdnUrl, ...library])]
                  }
                }
              });

              showSuccess({
                message: <Trans i18nKey="defaultGiftWrapSuccess" components={{ b: <b /> }} />,
                type: 'toast'
              });

              if (onUpdateData)
                onUpdateData({
                  giftWrapperUrl: cdnUrl
                });
            }}
          />
        </>
      )
    }
  ];

  return (
    <S.DigitalWrapper id={DIGITAL_WRAPPER} uploadDisabled={uploadAreaDisabled}>
      <S.TitleWrapper>
        <Typography color="fg" emphasis="weaker" variant="body2">
          {t('digitalWrapperDescription')}
        </Typography>
        {isPage && (
          <PreviewSection giftWrapperUrl={giftWrapperUrl} onSelectGiftWrapperUrl={onUpdateData} />
        )}
      </S.TitleWrapper>

      <SegmentedButton
        isPage
        activeTab={activeTab}
        onChange={setActiveTab}
        updateHash={false}
        options={wrapperOptions}
        withLine
        components={{
          FirstContent: () => (
            <S.DigitalWrapperFirstContent>
              <Typography>
                <Trans
                  i18nKey="giftWraps"
                  count={
                    activeTab === WRAPPERS_TAB.featured
                      ? dataDigitalWrappers?.pagination?.total
                      : libraryWrappers?.length
                  }
                  components={{ b: <b /> }}
                />
              </Typography>
              {hasOccasionOptions && configs?.experience?.digitalWrappersDropdownEnabled && (
                <S.DigitalWrapperFirstContentSelect>
                  <Typography color="fg" variant="body2" style={{ minWidth: 'fit-content' }}>
                    {t('selectOccasion')}:
                  </Typography>
                  <Select
                    placeholder={t('allOccasions')}
                    value={featuredOccasion}
                    size="sm"
                    menuPosition="absolute"
                    options={[{ label: t('allOccasions'), value: '' }, ...occasionOptions]}
                    onChange={selectedOccasion => {
                      setFeaturedOccasion(selectedOccasion);
                      setFeaturedPageIndex(1);
                    }}
                  />
                </S.DigitalWrapperFirstContentSelect>
              )}
              {activeTab === WRAPPERS_TAB.saved && (
                <Button
                  variant="link"
                  color="error"
                  startIcon={<Icon name="delete" />}
                  onClick={handleDeleteAll}
                  disabled={!libraryWrappers?.length}
                >
                  {t('deleteAll')}
                </Button>
              )}
            </S.DigitalWrapperFirstContent>
          )
        }}
        size="sm"
      />
    </S.DigitalWrapper>
  );
}

DigitalWrapper.propTypes = {
  branding: PropTypes.object,
  onUpdateData: PropTypes.func,
  isUpdating: PropTypes.bool,
  defaultOccasion: PropTypes.string,
  isPage: PropTypes.bool
};

export { DigitalWrapper };
